import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { CensusResponse, CaseWorkflowState } from '../../services/caseWorkflowService';
import {
    CENSUS_CAPTION, SINK_STATE_CAPTION, ageAccent, censusCount, count, hours, isSinkState
} from './caseWorkflowVocabulary';
import { dsVar, ink } from '../../theme/tokens';

interface CensusPanelProps {
    census: CensusResponse;
    states: CaseWorkflowState[];
    onSelect?: (state: string) => void;
}

/**
 * The backlog: what is sitting in each state right now.
 *
 * A different question from throughput, and neither implies the other — "40
 * cases moved to L2 this week" and "112 are sitting in L2 right now" are both
 * true at once. The two are deliberately not combined into one number.
 *
 * States are drawn in the registry's ordinal order rather than by size, so the
 * panel reads as a pipeline and a state holding nothing still appears.
 */
const CensusPanel: React.FC<CensusPanelProps> = ({ census, states, onSelect }) => {
    const rows = census.states ?? [];
    const max = Math.max(...rows.map((r) => censusCount(r)), 1);
    const caseGrain = census.grain === 'case';

    const ordered = [...states]
        .sort((a, b) => a.ordinal - b.ordinal)
        .map((state) => ({
            state,
            row: rows.find((r) => r.state === state.code) || null
        }));

    return (
        <Box>
            <Box className="flex flex-col gap-2.5">
                {ordered.map(({ state, row }) => {
                    const sink = isSinkState(state.code);
                    const value = censusCount(row);
                    const clickable = Boolean(onSelect) && value > 0;
                    return (
                        <Box
                            key={state.code}
                            onClick={clickable ? () => onSelect?.(state.code) : undefined}
                            className={clickable ? 'cursor-pointer' : ''}
                        >
                            <Box className="flex items-baseline justify-between gap-3 mb-1">
                                <Box className="flex items-center gap-1.5">
                                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-soft') }}>
                                        {state.label}
                                    </Typography>
                                    {sink && (
                                        <Tooltip title={SINK_STATE_CAPTION} arrow placement="top">
                                            <WarningAmberOutlinedIcon sx={{ fontSize: 13, color: ink('#FBBF24'), cursor: 'help' }} />
                                        </Tooltip>
                                    )}
                                </Box>
                                <Box className="flex items-baseline gap-2">
                                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), fontVariantNumeric: 'tabular-nums' }}>
                                        {count(value)}
                                    </Typography>
                                    {/*
                                      * The rows behind the cases, so "39" and the 1,746
                                      * records a drill-down opens reconcile on screen.
                                      */}
                                    {caseGrain && row && row.records !== value && (
                                        <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), fontVariantNumeric: 'tabular-nums' }}>
                                            ({count(row.records)} records)
                                        </Typography>
                                    )}
                                    {/*
                                      * A sink state is excluded from age entirely. Nothing
                                      * advances a record out of QA_Review, so its age grows
                                      * without bound and would read as a team falling behind.
                                      */}
                                    {row && !sink && (
                                        <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), fontVariantNumeric: 'tabular-nums' }}>
                                            {hours(row.p50AgeHours)} median age
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            <Box sx={{ height: 8, borderRadius: '4px', backgroundColor: dsVar('page'), overflow: 'hidden' }}>
                                <Box
                                    sx={{
                                        height: '100%',
                                        width: `${Math.max((value / max) * 100, value > 0 ? 2 : 0)}%`,
                                        backgroundColor: row ? ageAccent(row, rows) : 'transparent',
                                        borderRadius: '4px'
                                    }}
                                />
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {caseGrain && census.totalCases !== undefined && (
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mt: 2 }}>
                    {count(census.totalCases)} {census.totalCases === 1 ? 'case' : 'cases'} in total
                </Typography>
            )}
            <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: caseGrain ? 0.5 : 2 }}>
                {CENSUS_CAPTION} Age is coloured against the spread of the other states — no service targets are
                configured anywhere in the platform, so none are implied here.
            </Typography>
        </Box>
    );
};

export default CensusPanel;
